// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState, xCheckoutActions } from "@bg/App/Store/Reducers";
import { doScroll } from "@bg/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setIsPlacingOrder from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetIsPlacingOrder";
import setRequestedDeliveryDate from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetRequestedDeliveryDate";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { AnyAction, Dispatch } from "redux";

const mapStateToProps = (state: CustomAppState) => {
    const {
        accountSettings: { enableWarehousePickup, allowEmptyShipping },
        cartSettings: { canRequestDeliveryDate, enableRequestPickUpDate, maximumDeliveryPeriod },
    } = getSettingsCollection(state);
    const { fulfillmentMethod, pickUpWarehouse } = state.context.session;
    const { cartId } = state.pages.onePageCheckout;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const { value: cart, isLoading: isCartLoading } = cartState;
    return {
        cart,
        isCartLoading,
        showShippingMethod: fulfillmentMethod === FulfillmentMethod.Ship,
        showRequestPickUpDate:
            enableWarehousePickup && enableRequestPickUpDate && fulfillmentMethod === FulfillmentMethod.PickUp,
        showRequestDeliveryDate: canRequestDeliveryDate,
        maximumDeliveryPeriod,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        pickUpWarehouse,
        allowEmptyShipping,
        isPickUp: cart?.fulfillmentMethod === FulfillmentMethod.PickUp,
        isPlacingOrder: state.pages.onePageCheckout.isPlacingOrder,
        requestedDeliveryDateState: state.pages.onePageCheckout.requestedDeliveryDate,
        poNumber: state.xCheckout.po,
        notes: state.xCheckout.notes,
    };
};

const mapDispatchToProps = {
    setRequestedDeliveryDate,
    setCheckoutNotes: (notes: string) => (dispatch: Dispatch<xCheckoutActions>) => {
        dispatch({ type: "xCheckoutNotes/update", notes });
    },
    setPoState: (po: string) => (dispatch: Dispatch<xCheckoutActions>) => {
        dispatch({ type: "xCheckout/setPo", po });
    },
    resetIsPlacingOrder: (isPlacingOrder: boolean) => (dispatch: Dispatch<AnyAction>) => {
        dispatch(setIsPlacingOrder({ isPlacingOrder }) as any);
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OnePageCheckoutOrderDetailsStyles {
    container?: InjectableCss;
    titleText?: TypographyPresentationProps;
    poNumberText?: TextFieldPresentationProps;
    deliveryDateWrapper?: InjectableCss;
    deliveryDatePicker?: DatePickerPresentationProps;
    pickUpDateWrapper?: InjectableCss;
    pickUpDatePicker?: DatePickerPresentationProps;
    notesTextArea?: TextAreaProps;
    notesWrapper?: InjectableCss;
}

export const onePageCheckoutOrderDetailsStyles: OnePageCheckoutOrderDetailsStyles = {};

const styles = onePageCheckoutOrderDetailsStyles;
const OnePageCheckoutOrderDetails = ({
    cart,
    poNumber,
    showShippingMethod,
    showRequestPickUpDate,
    showRequestDeliveryDate,
    setRequestedDeliveryDate,
    notes,
    setCheckoutNotes,
    setPoState,
    isPickUp,
    resetIsPlacingOrder,
    isPlacingOrder,
    requestedDeliveryDateState,
}: Props) => {
    const [poNumberError, setPoNumberError] = useState("");
    const [dateStart, setDateStart] = useState<Date>();
    const [requestDateState, setRequestDateState] = useState<Date | undefined | null>(requestedDeliveryDateState);

    /**
     * XNG-Changes
     * Determine the earliest allowed delivery date:
     * - Today at 00:00 if before 11 AM
     * - Tomorrow at 00:00 otherwise
     */
    const getNextStartDate = (): Date => {
        const now = new Date();
        const start = new Date(now);

        if (now.getHours() > 11) {
            start.setDate(start.getDate() + 1);
        }

        start.setHours(0, 0, 0, 0);
        return start;
    };

    /**
     * XNG-Changes
     * Initialize start date
     */
    useEffect(() => {
        setDateStart(getNextStartDate());
    }, []);

    /**
     * XNG-Changes
     * Ensure requested date is valid (not in the past).
     */
    useEffect(() => {
        const validStart = getNextStartDate();

        if (requestedDeliveryDateState) {
            setRequestDateState(requestedDeliveryDateState);
            return;
        }

        if (cart?.requestedDeliveryDate) {
            const cartDate = new Date(cart.requestedDeliveryDate);
            const isPast = cartDate.getTime() < validStart.getTime();
            const finalDate = isPast ? validStart : cartDate;

            setRequestDateState(finalDate);
            setRequestedDeliveryDate({ requestedDeliveryDate: finalDate });
        }
    }, [cart]);

    useEffect(() => {
        if (cart?.requiresPoNumber && !poNumber && isPlacingOrder) {
            resetIsPlacingOrder(false);
            setPoNumberError(translate("Purchase Order number is required."));
            doScroll();
        }
        if (poNumber) {
            setPoNumberError("");
        }
    }, [poNumber, isPlacingOrder, cart?.requiresPoNumber]);

    useEffect(() => {
        if (cart?.notes && cart.notes !== notes) {
            setCheckoutNotes(cart.notes);
        }
    }, [cart?.notes]);

    useEffect(() => {
        if (cart?.poNumber && cart.poNumber !== poNumber) {
            setPoState(cart.poNumber);
        }
    }, [cart?.poNumber]);

    const handleRequestDeliveryDateChanged = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
        setRequestedDeliveryDate({
            requestedDeliveryDate: selectedDay,
            requestedDeliveryDateDisabled: selectedDayDisabled,
        });
    };

    const handlePoNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
        setPoState(event.target.value);
    };

    const handleNotesChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setCheckoutNotes(event.target.value);
    };

    if (!cart) {
        return null;
    }

    /**
     * XNG-Changes
     * They don't use "requestedPickupDateDisplay" in pickup, and should show "requestDateState" instead of it
     */
    return (
        <StyledWrapper {...styles.container} data-test-selector="OnePageCheckoutOrderDetails">
            <Typography {...styles.titleText}>{translate("Order Details")}</Typography>
            {cart.showPoNumber && !cart.requiresApproval && (
                <TextField
                    {...styles.poNumberText}
                    label={
                        <>
                            <span aria-hidden>{translate("Purchase Order")}</span>
                            <VisuallyHidden>{translate("Purchase Order Number")}</VisuallyHidden>
                        </>
                    }
                    value={poNumber}
                    onChange={handlePoNumberChange}
                    onBlur={() => setPoState(poNumber)}
                    required={cart.requiresPoNumber}
                    maxLength={25}
                    placeholder={translate("Enter Purchase Order")}
                    data-test-selector="onePageCheckout_poNumber"
                    error={poNumberError}
                />
            )}
            {((showShippingMethod && showRequestDeliveryDate) || showRequestPickUpDate) && !cart.requiresApproval && (
                <StyledWrapper
                    {...styles.deliveryDateWrapper}
                    data-test-selector="onePageCheckout_requestedDeliveryDate"
                >
                    <DatePicker
                        {...styles.deliveryDatePicker}
                        label={translate("Request Date")}
                        hint={siteMessage("Checkout_RequestedDeliveryDateInformation")}
                        selectedDay={requestDateState || undefined}
                        dateTimePickerProps={{
                            minDate: dateStart,
                            monthAriaLabel: translate("Request date month"),
                            dayAriaLabel: translate("Request date day"),
                            yearAriaLabel: translate("Request date year"),
                            ...styles.deliveryDatePicker?.dateTimePickerProps,
                        }}
                        onDayChange={handleRequestDeliveryDateChanged}
                    />
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.notesWrapper}>
                <TextArea
                    label={translate("Order Notes")}
                    {...styles.notesTextArea}
                    value={notes}
                    onChange={handleNotesChange}
                    onBlur={() => setCheckoutNotes(notes ?? "")}
                    rows={3}
                    maxLength={150}
                    data-test-selector="onepagecheckout_notes"
                    placeholder={translate("You can request a time for order {0}", isPickUp ? "pickup" : "delivery")}
                />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OnePageCheckoutOrderDetails),
    definition: {
        displayName: "Order Details",
        group: "One Page Checkout",
        allowedContexts: ["OnePageCheckoutPage"],
    },
};

export default widgetModule;
